/**
 * TRIX
 *
 * Triple exponential smoothed rate of change.
 * Filters insignificant price moves and highlights trend direction.
 * TRIX = 100 × (EMA3[i] − EMA3[i−1]) / EMA3[i−1]
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource, computeEMA } from "./sma";

export const trixIndicator: IndicatorDefinition = {
  id: "trix",
  name: "TRIX",
  shortName: "TRIX",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 18, min: 2, max: 200 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#E91E63" },
  ],
  plots: [
    { key: "trix", label: "TRIX", color: "#E91E63", style: "line", lineWidth: 2, priceScaleId: "trix" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    const ema1 = computeEMA(src, period);
    const ema1Valid = ema1.filter(v => !isNaN(v));
    const ema2 = computeEMA(ema1Valid, period);
    const ema2Valid = ema2.filter(v => !isNaN(v));
    const ema3 = computeEMA(ema2Valid, period);

    const offset = candles.length - ema3.filter(v => !isNaN(v)).length;
    let validIdx = 0;
    for (let i = 0; i < ema3.length; i++) {
      if (!isNaN(ema3[i])) {
        if (validIdx > 0) {
          // Find previous valid value
          let prevVal = NaN;
          for (let j = i - 1; j >= 0; j--) {
            if (!isNaN(ema3[j])) { prevVal = ema3[j]; break; }
          }
          if (!isNaN(prevVal) && prevVal !== 0) {
            const candleIdx = offset + validIdx;
            if (candleIdx < candles.length) {
              data.push({ time: candles[candleIdx].time, value: 100 * (ema3[i] - prevVal) / prevVal });
            }
          }
        }
        validIdx++;
      }
    }

    return [{ key: "trix", data }];
  },
};
